/**
 * Sanitizes text returned by an LLM so it renders safely inside HTML table cells.
 *
 * Handles:
 *  - null / undefined / non-string values
 *  - Markdown bold / italic / strikethrough / inline code
 *  - Markdown links [text](url) → text
 *  - Markdown headers (# ...)
 *  - Smart (curly) quotes → straight quotes
 *  - Em / en dashes → hyphens
 *  - Stray HTML tags
 *  - Newlines, tabs, and excessive whitespace
 */
import sanitizeHtml from 'sanitize-html';

const sanitizeText = (value) => {
  if (value === null || value === undefined) return '';
  const str = typeof value === 'string' ? value : String(value);

  const htmlStripped = sanitizeHtml(str, {
    allowedTags: [],
    allowedAttributes: {}
  });

  return htmlStripped
    // Strip markdown links [text](url) ? text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    // Strip markdown bold / italic (***bold italic***, **bold**, *italic*, __bold__, _italic_)
    .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1')
    .replace(/_{1,3}([^_]+)_{1,3}/g, '$1')
    // Strip markdown strikethrough ~~text~~
    .replace(/~~([^~]+)~~/g, '$1')
    // Strip markdown inline code `code`
    .replace(/`([^`]*)`/g, '$1')
    // Strip markdown headers (e.g. "## Title")
    .replace(/^#{1,6}\s+/gm, '')
    // Smart quotes ? straight quotes
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    // En dash / Em dash ? hyphen
    .replace(/[\u2013\u2014]/g, '-')
    // Horizontal ellipsis ? three dots
    .replace(/\u2026/g, '...')
    // Collapse newlines, carriage returns, tabs into a single space
    .replace(/[\r\n\t]+/g, ' ')
    // Collapse multiple spaces into one
    .replace(/ {2,}/g, ' ')
    .trim();
};

export default sanitizeText;
